import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { db, supabase } from '../../lib/supabase';

const Messages = () => {
  const { currentUser, students, refreshUnreadCount, cohorts } = useLMS();
  
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [replyMode, setReplyMode] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  
  // Compose form state
  const [composeData, setComposeData] = useState({
    recipientId: '',
    subject: '',
    body: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const isAdmin = currentUser?.role === 'admin';
  const isStaff = isAdmin || currentUser?.role === 'professor';
  
  // Load all users for recipient dropdown
  useEffect(() => {
    const loadAllUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, name, email, role, avatar_url, cohort_id');
        
        if (!error && data) {
          setAllUsers(data);
        }
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };
    loadAllUsers();
  }, []);
  
  // Get available recipients based on user role
  const getRecipients = () => {
    const users = allUsers.length > 0 ? allUsers : (students || []);
    if (users.length === 0) return [];
    
    if (isAdmin) {
      // Admins can message other admins + all students
      const otherAdmins = users.filter(s => s.role === 'admin' && s.id !== currentUser?.id);
      const allStudents = users.filter(s => s.role === 'student');
      return { admins: otherAdmins, students: allStudents };
    } else {
      // Students can message admins + students in their cohort
      const admins = users.filter(s => s.role === 'admin');
      const cohortStudents = users.filter(s => 
        s.role === 'student' && 
        s.cohort_id === currentUser?.cohortId &&
        s.id !== currentUser?.id
      );
      return { admins, cohortStudents };
    }
  };

  const recipientGroups = getRecipients();
  
  // Get cohort name helper
  const getCohortName = (cohortId) => {
    if (!cohortId) return 'Unassigned';
    // Try to find from allUsers or use ID
    const cohort = cohorts?.find(c => c.id === cohortId);
    return cohort?.name || 'Unknown Cohort';
  };
  
  // Group students by cohort for admin view
  const getStudentsByCohort = () => {
    if (!isAdmin || !recipientGroups.students) return {};
    const grouped = {};
    recipientGroups.students.forEach(student => {
      const cohortName = getCohortName(student.cohort_id);
      if (!grouped[cohortName]) grouped[cohortName] = [];
      grouped[cohortName].push(student);
    });
    return grouped;
  };
  
  const studentsByCohort = getStudentsByCohort();

  // Load messages
  useEffect(() => {
    if (!currentUser?.id) return;
    loadMessages();
  }, [currentUser?.id, activeTab]);

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      let result;
      if (activeTab === 'inbox') {
        result = await db.getInboxMessages(currentUser.id);
      } else {
        result = await db.getSentMessages(currentUser.id);
      }
      
      if (result.error) {
        console.error('Error loading messages:', result.error);
      } else {
        setMessages(result.data || []);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
    setIsLoading(false);
  };

  // Load conversation when message is selected
  const loadConversation = async (message) => {
    setSelectedMessage(message);
    
    // Mark as read if unread
    if (!message.is_read && message.recipient_id === currentUser.id) {
      await db.markMessageRead(message.id);
      // Update local state
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, is_read: true } : m
      ));
      // Update unread count in sidebar
      if (refreshUnreadCount) refreshUnreadCount();
    }
    
    // Load full conversation thread
    const { data, error } = await db.getConversation(message.id, currentUser.id);
    if (!error && data) {
      setConversation(data);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!composeData.recipientId || !composeData.subject.trim() || !composeData.body.trim()) {
      setSendError('Please fill in all fields');
      return;
    }

    setIsSending(true);
    setSendError('');

    try {
      const parentId = replyMode && selectedMessage ? selectedMessage.id : null;
      const { data, error } = await db.sendMessage(
        currentUser.id,
        composeData.recipientId,
        composeData.subject,
        composeData.body,
        parentId
      );

      if (error) throw error;

      // Reset form and close modal
      setComposeData({ recipientId: '', subject: '', body: '' });
      setShowComposeModal(false);
      setReplyMode(false);
      
      // Reload messages
      loadMessages();
      
      // If replying, reload conversation
      if (parentId && selectedMessage) {
        loadConversation(selectedMessage);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSendError(error.message || 'Failed to send message');
    }
    
    setIsSending(false);
  };

  // Open reply modal
  const handleReply = () => {
    if (!selectedMessage) return;
    
    // Determine who to reply to
    const replyToId = selectedMessage.sender_id === currentUser.id 
      ? selectedMessage.recipient_id 
      : selectedMessage.sender_id;
    
    setComposeData({
      recipientId: replyToId,
      subject: selectedMessage.subject.startsWith('Re: ') 
        ? selectedMessage.subject 
        : `Re: ${selectedMessage.subject}`,
      body: ''
    });
    setReplyMode(true);
    setShowComposeModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="messages-view">
      {/* Header */}
      <div className="messages-header">
        <div className="header-left">
          <h1>✉️ Messages</h1>
        </div>
        <button 
          className="btn-primary"
          onClick={() => {
            setComposeData({ recipientId: '', subject: '', body: '' });
            setReplyMode(false);
            setShowComposeModal(true);
          }}
        >
          ✏️ Compose
        </button>
      </div>

      {/* Main Layout */}
      <div className="messages-layout">
        {/* Sidebar */}
        <div className="messages-sidebar">
          <div className="messages-tabs">
            <button 
              className={`tab-btn ${activeTab === 'inbox' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('inbox');
                setSelectedMessage(null);
              }}
            >
              📥 Inbox
              {messages.filter(m => !m.is_read && activeTab === 'inbox').length > 0 && (
                <span className="unread-badge">
                  {messages.filter(m => !m.is_read).length}
                </span>
              )}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'sent' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('sent');
                setSelectedMessage(null);
              }}
            >
              📤 Sent
            </button>
          </div>

          {/* Message List */}
          <div className="messages-list">
            {isLoading ? (
              <div className="loading-state">Loading...</div>
            ) : messages.length === 0 ? (
              <div className="empty-state small">
                <span className="empty-icon">📭</span>
                <p>No messages</p>
              </div>
            ) : (
              messages.map(message => {
                const otherPerson = activeTab === 'inbox' ? message.sender : message.recipient;
                const isSelected = selectedMessage?.id === message.id;
                const isUnread = !message.is_read && activeTab === 'inbox';
                
                return (
                  <div 
                    key={message.id}
                    className={`message-item ${isSelected ? 'selected' : ''} ${isUnread ? 'unread' : ''}`}
                    onClick={() => loadConversation(message)}
                  >
                    <div className="message-avatar">
                      {otherPerson?.avatar_url ? (
                        <img src={otherPerson.avatar_url} alt={otherPerson.name} />
                      ) : (
                        <div className="avatar-placeholder">
                          {(otherPerson?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="message-preview">
                      <div className="message-header-row">
                        <span className={`message-sender ${isUnread ? 'bold' : ''}`}>
                          {otherPerson?.name || 'Unknown'}
                        </span>
                        <span className="message-date">{formatDate(message.created_at)}</span>
                      </div>
                      <div className={`message-subject ${isUnread ? 'bold' : ''}`}>
                        {message.subject}
                      </div>
                      <div className="message-snippet">
                        {message.body.substring(0, 60)}{message.body.length > 60 ? '...' : ''}
                      </div>
                    </div>
                    {isUnread && <div className="unread-dot"></div>}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Message Content */}
        <div className="message-content">
          {selectedMessage ? (
            <>
              <div className="message-content-header">
                <h2>{selectedMessage.subject}</h2>
                <button className="btn-secondary" onClick={handleReply}>
                  ↩️ Reply
                </button>
              </div>
              
              <div className="conversation-thread">
                {conversation.map((msg, index) => {
                  const isSender = msg.sender_id === currentUser.id;
                  const person = isSender ? msg.sender : msg.sender;
                  
                  return (
                    <div key={msg.id} className={`conversation-message ${isSender ? 'sent' : 'received'}`}>
                      <div className="conversation-header">
                        <div className="sender-info">
                          {person?.avatar_url ? (
                            <img src={person.avatar_url} alt={person.name} className="sender-avatar" />
                          ) : (
                            <div className="avatar-placeholder small">
                              {(person?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                            </div>
                          )}
                          <span className="sender-name">{person?.name}</span>
                          {(person?.role === 'admin' || person?.role === 'professor') && <span className="role-tag">{person?.role === 'professor' ? 'Professor' : 'Instructor'}</span>}
                        </div>
                        <span className="message-time">{formatFullDate(msg.created_at)}</span>
                      </div>
                      <div className="conversation-body">
                        {msg.body.split('\n').map((line, i) => (
                          <p key={i}>{line || <br />}</p>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick Reply */}
              <div className="quick-reply">
                <textarea 
                  placeholder="Write a reply..."
                  value={composeData.body}
                  onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey) {
                      // Set up reply data and send
                      const replyToId = selectedMessage.sender_id === currentUser.id 
                        ? selectedMessage.recipient_id 
                        : selectedMessage.sender_id;
                      setComposeData(prev => ({
                        ...prev,
                        recipientId: replyToId,
                        subject: selectedMessage.subject.startsWith('Re: ') 
                          ? selectedMessage.subject 
                          : `Re: ${selectedMessage.subject}`
                      }));
                      // Need to wait for state update
                    }
                  }}
                ></textarea>
                <button 
                  className="btn-primary"
                  disabled={!composeData.body.trim()}
                  onClick={() => {
                    const replyToId = selectedMessage.sender_id === currentUser.id 
                      ? selectedMessage.recipient_id 
                      : selectedMessage.sender_id;
                    setComposeData(prev => ({
                      ...prev,
                      recipientId: replyToId,
                      subject: selectedMessage.subject.startsWith('Re: ') 
                        ? selectedMessage.subject 
                        : `Re: ${selectedMessage.subject}`
                    }));
                    setReplyMode(true);
                    // Trigger send after state updates
                    setTimeout(() => {
                      document.getElementById('quick-send-btn')?.click();
                    }, 100);
                  }}
                >
                  Send
                </button>
                <button 
                  id="quick-send-btn"
                  style={{ display: 'none' }}
                  onClick={handleSendMessage}
                />
              </div>
            </>
          ) : (
            <div className="no-message-selected">
              <span className="empty-icon">✉️</span>
              <p>Select a message to read</p>
            </div>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {showComposeModal && (
        <div className="modal-overlay" onClick={() => setShowComposeModal(false)}>
          <div className="modal compose-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{replyMode ? 'Reply' : 'New Message'}</h3>
              <button className="modal-close" onClick={() => setShowComposeModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSendMessage}>
              <div className="modal-body">
                {sendError && (
                  <div className="error-message">{sendError}</div>
                )}
                
                <div className="form-group">
                  <label>To</label>
                  <select 
                    value={composeData.recipientId}
                    onChange={(e) => setComposeData(prev => ({ ...prev, recipientId: e.target.value }))}
                    required
                    disabled={replyMode}
                  >
                    <option value="">Select recipient...</option>
                    
                    {/* Admins/Instructors group */}
                    {recipientGroups.admins && recipientGroups.admins.length > 0 && (
                      <optgroup label="📚 Instructors">
                        {recipientGroups.admins.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    
                    {/* For students: show cohort students */}
                    {!isAdmin && recipientGroups.cohortStudents && recipientGroups.cohortStudents.length > 0 && (
                      <optgroup label="👥 My Cohort">
                        {recipientGroups.cohortStudents.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    
                    {/* For admins: show students grouped by cohort */}
                    {isAdmin && Object.keys(studentsByCohort).map(cohortName => (
                      <optgroup key={cohortName} label={`🎓 ${cohortName}`}>
                        {studentsByCohort[cohortName].map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Subject</label>
                  <input 
                    type="text"
                    value={composeData.subject}
                    onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Enter subject..."
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Message</label>
                  <textarea 
                    value={composeData.body}
                    onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Write your message..."
                    rows={8}
                    required
                  ></textarea>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowComposeModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSending}
                >
                  {isSending ? 'Sending...' : '📤 Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
